import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, UserPlus, CheckCircle2, XCircle } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";
import { useNavigate } from "react-router-dom";

interface Contact {
  id: number;
  company: string;
  firstName: string;
  lastName: string;
  entityType: string;
  region: string;
  state: string;
  assigned_to: string | null;
}

interface ClientUser {
  id: string;
  email: string;
  full_name: string;
}

export default function ContactAssignments() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [clients, setClients] = useState<ClientUser[]>([]);
  const [selectedContact, setSelectedContact] = useState<number | null>(null);
  const [selectedClient, setSelectedClient] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const { toast } = useToast();
  const { role, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();

  useEffect(() => {
    if (!roleLoading && role !== 'admin' && role !== 'editor') {
      toast({
        title: "Acesso negado",
        description: "Apenas administradores e editores podem acessar esta página",
        variant: "destructive"
      });
      navigate("/");
    }
  }, [role, roleLoading, navigate, toast]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Buscar contatos
      const { data: contactsData, error: contactsError } = await supabase
        .from('contacts')
        .select('id, company, first_name, last_name, entity_type, region, state, assigned_to')
        .order('company');

      if (contactsError) throw contactsError;

      setContacts(contactsData.map(c => ({
        id: c.id,
        company: c.company,
        firstName: c.first_name,
        lastName: c.last_name,
        entityType: c.entity_type,
        region: c.region || '',
        state: c.state || '',
        assigned_to: c.assigned_to
      })));

      // Buscar usuários com role 'cliente'
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'cliente');

      if (rolesError) throw rolesError;

      const clientIds = rolesData.map(r => r.user_id);

      if (clientIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('user_profiles')
          .select('id, full_name')
          .in('id', clientIds);

        if (profilesError) throw profilesError;

        // Buscar emails do auth.users via RPC ou admin endpoint
        const { data: { user } } = await supabase.auth.getUser();
        
        const clientsWithEmails = profilesData.map(profile => ({
          id: profile.id,
          email: profile.id, // Fallback to ID if email not available
          full_name: profile.full_name || 'Sem nome'
        }));

        setClients(clientsWithEmails);
      }

    } catch (error: any) {
      console.error('Erro ao carregar dados:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedContact || !selectedClient) {
      toast({
        title: "Campos obrigatórios",
        description: "Selecione um contato e um cliente",
        variant: "destructive"
      });
      return;
    }

    try {
      setAssigning(true);

      const { error } = await supabase.rpc('assign_contact_to_client', {
        _contact_id: selectedContact,
        _client_user_id: selectedClient,
        _notes: notes || null
      });

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Contato atribuído com sucesso",
      });

      // Limpar seleções
      setSelectedContact(null);
      setSelectedClient("");
      setNotes("");
      
      // Recarregar dados
      fetchData();

    } catch (error: any) {
      console.error('Erro ao atribuir contato:', error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível atribuir o contato",
        variant: "destructive"
      });
    } finally {
      setAssigning(false);
    }
  };

  const unassignedContacts = contacts.filter(c => !c.assigned_to);
  const assignedContacts = contacts.filter(c => c.assigned_to);

  if (roleLoading || loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AppSidebar onClearData={async () => {}} />
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto space-y-8">
            <div>
              <h1 className="text-3xl font-bold">Atribuição de Contatos</h1>
              <p className="text-muted-foreground mt-2">
                Atribua contatos a clientes específicos para controlar o acesso aos dados
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserPlus className="h-5 w-5" />
                    Atribuir Contato
                  </CardTitle>
                  <CardDescription>
                    Selecione um contato e um cliente para criar a atribuição
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Contato</label>
                    <Select
                      value={selectedContact?.toString() || ""}
                      onValueChange={(value) => setSelectedContact(parseInt(value))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um contato" />
                      </SelectTrigger>
                      <SelectContent>
                        {unassignedContacts.map((contact) => (
                          <SelectItem key={contact.id} value={contact.id.toString()}>
                            {contact.company} - {contact.firstName} {contact.lastName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">Cliente</label>
                    <Select
                      value={selectedClient}
                      onValueChange={setSelectedClient}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">Observações (opcional)</label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Motivo da atribuição..."
                      rows={3}
                    />
                  </div>

                  <Button
                    onClick={handleAssign}
                    disabled={assigning || !selectedContact || !selectedClient}
                    className="w-full"
                  >
                    {assigning ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Atribuindo...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Atribuir Contato
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Estatísticas</CardTitle>
                  <CardDescription>Visão geral das atribuições</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                    <div>
                      <p className="text-sm text-muted-foreground">Total de Contatos</p>
                      <p className="text-2xl font-bold">{contacts.length}</p>
                    </div>
                    <CheckCircle2 className="h-8 w-8 text-muted-foreground" />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                    <div>
                      <p className="text-sm text-green-700 dark:text-green-300">Atribuídos</p>
                      <p className="text-2xl font-bold text-green-700 dark:text-green-300">
                        {assignedContacts.length}
                      </p>
                    </div>
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-orange-50 dark:bg-orange-950 rounded-lg">
                    <div>
                      <p className="text-sm text-orange-700 dark:text-orange-300">Não Atribuídos</p>
                      <p className="text-2xl font-bold text-orange-700 dark:text-orange-300">
                        {unassignedContacts.length}
                      </p>
                    </div>
                    <XCircle className="h-8 w-8 text-orange-600" />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                    <div>
                      <p className="text-sm text-blue-700 dark:text-blue-300">Clientes Ativos</p>
                      <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                        {clients.length}
                      </p>
                    </div>
                    <UserPlus className="h-8 w-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Contatos Não Atribuídos</CardTitle>
                <CardDescription>
                  {unassignedContacts.length} contato(s) disponível(is) para atribuição
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {unassignedContacts.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      Todos os contatos foram atribuídos
                    </p>
                  ) : (
                    unassignedContacts.map((contact) => (
                      <div
                        key={contact.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                        onClick={() => setSelectedContact(contact.id)}
                      >
                        <div>
                          <p className="font-medium">{contact.company}</p>
                          <p className="text-sm text-muted-foreground">
                            {contact.firstName} {contact.lastName} • {contact.state}
                          </p>
                        </div>
                        <Badge variant="secondary">{contact.entityType}</Badge>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
